import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import {
  getTransfeeroConfig,
  updateTransfeeroConfig,
  syncTransfeeroNow,
  simulateTransfeeroOffer,
  getTransfeeroLogs,
  downloadTransfeeroExtensionZip
} from '../api';

const DECISION_META = {
  AUTO_ACCEPT: { icon: '✅', label: 'Auto-Aceptada', bg: 'success' },
  ALERT_LOCUCION: { icon: '🚨', label: 'Alerta Locución (1%-9%)', bg: 'warning', text: 'dark' },
  REJECT_MARGIN: { icon: '⛔', label: 'Bajo Costo (<1%)', bg: 'danger' },
  REJECT_LEAD_TIME: { icon: '⏱️', label: 'Anticipación < Mínimo', bg: 'secondary' },
  REJECT_ZONE: { icon: '📍', label: 'Fuera de Zona / Sin Tarifa', bg: 'dark' },
  MONITOR_ONLY: { icon: '👁️', label: 'Solo Monitoreo', bg: 'info', text: 'dark' }
};

const formatCLP = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return '—';
  return `$${Math.round(Number(val)).toLocaleString('es-CL')}`;
};

const formatUSD = (val, curr = 'USD') => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return '—';
  return `${curr} ${Number(val).toFixed(2)}`;
};

const defaultSimDateTime = () => {
  const d = new Date(Date.now() + 8 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TransfeeroManagerView = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloadingExt, setDownloadingExt] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [configData, setConfigData] = useState(null);
  const [formState, setFormState] = useState({
    is_auto_accept_enabled: true,
    is_movio_sync_enabled: true,
    movio_sync_interval_minutes: 3,
    movio_email: '',
    movio_password: '',
    offer_currency: 'USD',
    custom_usd_rate: '',
    min_lead_time_hours: 4.0,
    auto_accept_min_margin_pct: 10.0,
    locucion_alert_min_margin_pct: 1.0,
    locucion_alert_max_margin_pct: 9.99,
    allowed_base_ids: [1],
    human_delay_min_sec: 3.8,
    human_delay_max_sec: 6.2,
    vehicle_class_mapping: {}
  });

  const [feedback, setFeedback] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [logsPayload, setLogsPayload] = useState({ stats: {}, items: [] });
  const [decisionFilter, setDecisionFilter] = useState('');

  const [simForm, setSimForm] = useState({
    pickup_address: 'Aeropuerto Arturo Merino Benítez (SCL), Pudahuel, Santiago',
    dropoff_address: 'Av. Andrés Bello 2425, Providencia, Santiago',
    pickup_datetime: defaultSimDateTime(),
    vehicle_class: 'Standard',
    passengers: 2,
    offer_price: 35.0,
    currency: 'USD'
  });
  const [simResult, setSimResult] = useState(null);

  const loadAll = useCallback(async (filterVal = decisionFilter) => {
    try {
      const [cfg, logsRes] = await Promise.all([
        getTransfeeroConfig(),
        getTransfeeroLogs({ limit: 80, decision: filterVal || undefined })
      ]);
      setConfigData(cfg);
      setFormState({
        is_auto_accept_enabled: Boolean(cfg.is_auto_accept_enabled),
        is_movio_sync_enabled: Boolean(cfg.is_movio_sync_enabled),
        movio_sync_interval_minutes: cfg.movio_sync_interval_minutes ?? 3,
        movio_email: cfg.movio_email || '',
        movio_password: '',
        offer_currency: cfg.offer_currency || 'USD',
        custom_usd_rate: cfg.custom_usd_rate ?? '',
        min_lead_time_hours: cfg.min_lead_time_hours ?? 4.0,
        auto_accept_min_margin_pct: cfg.auto_accept_min_margin_pct ?? 10.0,
        locucion_alert_min_margin_pct: cfg.locucion_alert_min_margin_pct ?? 1.0,
        locucion_alert_max_margin_pct: cfg.locucion_alert_max_margin_pct ?? 9.99,
        allowed_base_ids: Array.isArray(cfg.allowed_base_ids) && cfg.allowed_base_ids.length ? cfg.allowed_base_ids : [1],
        human_delay_min_sec: cfg.human_delay_min_sec ?? 3.8,
        human_delay_max_sec: cfg.human_delay_max_sec ?? 6.2,
        vehicle_class_mapping: cfg.vehicle_class_mapping || {}
      });
      setLogsPayload(logsRes || { stats: {}, items: [] });
    } catch (e) {
      setFeedback({ variant: 'danger', text: 'No se pudo cargar la configuración de Transfeero.' });
    } finally {
      setLoading(false);
    }
  }, [decisionFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleToggleBase = (baseId) => {
    setFormState((prev) => {
      const exists = prev.allowed_base_ids.includes(baseId);
      const next = exists
        ? prev.allowed_base_ids.filter((id) => id !== baseId)
        : [...prev.allowed_base_ids, baseId];
      return { ...prev, allowed_base_ids: next.length ? next : [baseId] };
    });
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const payload = {
        ...formState,
        movio_sync_interval_minutes: Number(formState.movio_sync_interval_minutes) || 3,
        custom_usd_rate: formState.custom_usd_rate === '' ? 0 : Number(formState.custom_usd_rate),
        min_lead_time_hours: Number(formState.min_lead_time_hours),
        auto_accept_min_margin_pct: Number(formState.auto_accept_min_margin_pct),
        locucion_alert_min_margin_pct: Number(formState.locucion_alert_min_margin_pct),
        locucion_alert_max_margin_pct: Number(formState.locucion_alert_max_margin_pct),
        human_delay_min_sec: Number(formState.human_delay_min_sec),
        human_delay_max_sec: Number(formState.human_delay_max_sec)
      };
      if (!payload.movio_password) {
        delete payload.movio_password;
      }
      const updated = await updateTransfeeroConfig(payload);
      setConfigData(updated);
      setFormState((prev) => ({ ...prev, movio_password: '' }));
      setFeedback({ variant: 'success', text: '✅ Reglas y credenciales guardadas correctamente en EmiGo.' });
    } catch (err) {
      setFeedback({ variant: 'danger', text: 'Error al guardar los cambios.' });
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await syncTransfeeroNow();
      setSyncResult(res);
      await loadAll();
    } catch (err) {
      setFeedback({
        variant: 'danger',
        text: err?.response?.data?.detail || 'Error al sincronizar con Movio Cloud.'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadExtension = async () => {
    setDownloadingExt(true);
    try {
      await downloadTransfeeroExtensionZip();
    } finally {
      setDownloadingExt(false);
    }
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    setSimulating(true);
    setSimResult(null);
    try {
      const res = await simulateTransfeeroOffer({
        ...simForm,
        passengers: Number(simForm.passengers) || 1,
        offer_price: Number(simForm.offer_price) || 0
      });
      setSimResult(res);
    } catch (err) {
      setFeedback({ variant: 'danger', text: 'No se pudo ejecutar la simulación.' });
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-5 text-center">
        <Spinner animation="border" variant="success" />
        <p className="mt-2 text-muted">Cargando mantenedor de Transfeero y Movio Cloud...</p>
      </div>
    );
  }

  const extOnline = (() => {
    if (!configData?.last_extension_heartbeat_at) return false;
    const diffMs = Date.now() - new Date(configData.last_extension_heartbeat_at).getTime();
    return diffMs < 3 * 60 * 1000;
  })();

  const availableBases = configData?.available_bases || [{ id: 1, name: 'CL - Santiago' }];
  const vehicleTypes = configData?.available_vehicle_types || [];
  const stats = logsPayload?.stats || {};

  return (
    <div className="pb-4">
      {/* Cabecera de estado operativo */}
      <Card className="border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)', color: '#f8fafc' }}>
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h4 className="mb-0 fw-bold text-white">🚘 Mantenedor Transfeero &amp; Centinela Humanizado</h4>
                <Badge bg={configData?.is_auto_accept_enabled ? 'success' : 'secondary'}>
                  {configData?.is_auto_accept_enabled ? '⚡ Auto-Accept Activo' : '👁️ Solo Monitoreo'}
                </Badge>
                <Badge bg={extOnline ? 'success' : 'dark'} className="border border-secondary">
                  {extOnline
                    ? `🟢 Extensión Chrome Conectada (${configData?.last_extension_heartbeat_cl})`
                    : `⚪ Extensión Sin Latido Reciente${configData?.last_extension_heartbeat_cl ? ` (${configData.last_extension_heartbeat_cl})` : ''}`}
                </Badge>
              </div>
              <div className="small mt-2" style={{ color: '#cbd5e1' }}>
                Sincronización 24/7 vía <strong>Movio Free Cloud</strong> ({configData?.last_movio_sync_status || 'Sin sincronizar aún'}
                {configData?.last_movio_sync_cl ? ` · Última: ${configData.last_movio_sync_cl}` : ''}) · Dólar EmiGo activo:{' '}
                <strong>{formatCLP(configData?.effective_usd_to_clp_rate)} / USD</strong>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="success"
                className="fw-bold shadow-sm"
                onClick={handleManualSync}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Sincronizando Movio...
                  </>
                ) : (
                  '🔄 Sincronizar Reservas Ahora'
                )}
              </Button>
              <Button
                variant="outline-light"
                className="fw-semibold"
                onClick={handleDownloadExtension}
                disabled={downloadingExt}
              >
                {downloadingExt ? 'Empaquetando...' : '📦 Descargar Extensión Chrome (.zip)'}
              </Button>
            </div>
          </div>

          {syncResult && (
            <Alert variant="info" className="mt-3 mb-0 py-2 px-3 border-0" onClose={() => setSyncResult(null)} dismissible>
              <strong>Sincronización completada ({syncResult.synced_at_cl}):</strong>{' '}
              Escaneadas: <strong>{syncResult.total_scanned}</strong> · Nuevas ingresadas:{' '}
              <strong>{syncResult.created}</strong> · Actualizadas/Vinculadas:{' '}
              <strong>{syncResult.updated}</strong> · Canceladas: <strong>{syncResult.cancelled}</strong> · Sin cambios:{' '}
              <strong>{syncResult.unchanged}</strong>
              {Array.isArray(syncResult.details) && syncResult.details.length > 0 && (
                <ul className="mb-0 mt-1 small">
                  {syncResult.details.map((d, idx) => (
                    <li key={idx}>
                      <strong>#{d.booking_code}</strong> (Viaje EmiGo #{d.trip_id}): {d.action}
                      {d.changes ? ` — ${d.changes.join(' | ')}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {feedback && (
        <Alert variant={feedback.variant} onClose={() => setFeedback(null)} dismissible>
          {feedback.text}
        </Alert>
      )}

      {/* KPIs rápidos */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="py-3">
              <div className="text-muted small fw-semibold">Ofertas Evaluadas</div>
              <div className="fs-4 fw-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{stats.total_evaluated || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-4 border-success">
            <Card.Body className="py-3">
              <div className="text-muted small fw-semibold">✅ Auto-Aceptadas (&ge;+{formState.auto_accept_min_margin_pct}%)</div>
              <div className="fs-4 fw-bold text-success" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {stats.auto_accepted || 0}{' '}
                <span className="fs-6 text-muted fw-normal">({stats.portal_confirmed || 0} ganadas)</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-4 border-warning">
            <Card.Body className="py-3">
              <div className="text-muted small fw-semibold">🚨 Alertas a Locución ({formState.locucion_alert_min_margin_pct}%–{formState.locucion_alert_max_margin_pct}%)</div>
              <div className="fs-4 fw-bold text-warning" style={{ fontVariantNumeric: 'tabular-nums' }}>{stats.locucion_alerted || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-4 border-secondary">
            <Card.Body className="py-3">
              <div className="text-muted small fw-semibold">⛔ Descartadas (Costo / Hora / Zona)</div>
              <div className="fs-4 fw-bold text-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>{stats.rejected || 0}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        {/* Formulario Principal de Reglas y Credenciales */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <form onSubmit={handleSaveConfig}>
                <fieldset className="mb-4 border-0 p-0 m-0">
                  <legend className="fs-5 fw-bold mb-3 pb-2 border-bottom">
                    ⚙️ Reglas de Negocio y Rentabilidad sobre Costo de Zona
                  </legend>

                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <div className="form-check form-switch p-3 rounded bg-light border">
                        <input
                          className="form-check-input ms-0 me-2"
                          type="checkbox"
                          id="tf-auto-accept-switch"
                          checked={formState.is_auto_accept_enabled}
                          onChange={(e) => setFormState({ ...formState, is_auto_accept_enabled: e.target.checked })}
                        />
                        <label className="form-check-label fw-bold" htmlFor="tf-auto-accept-switch">
                          Auto-Aceptación Humana Activa
                        </label>
                        <div className="small text-muted mt-1">
                          Si se apaga, el Centinela solo registrará y alertará sin hacer clic en Accept.
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-check form-switch p-3 rounded bg-light border">
                        <input
                          className="form-check-input ms-0 me-2"
                          type="checkbox"
                          id="tf-movio-sync-switch"
                          checked={formState.is_movio_sync_enabled}
                          onChange={(e) => setFormState({ ...formState, is_movio_sync_enabled: e.target.checked })}
                        />
                        <label className="form-check-label fw-bold" htmlFor="tf-movio-sync-switch">
                          Sincronización Nube Movio 24/7
                        </label>
                        <div className="small text-muted mt-1">
                          Importa reservas ganadas, cambios de horario/vuelo y cancelaciones automáticamente.
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col sm={6} md={4}>
                      <label htmlFor="tf-min-lead" className="form-label fw-semibold small mb-1">
                        Anticipación Mínima (horas)
                      </label>
                      <input
                        id="tf-min-lead"
                        type="number"
                        step="0.5"
                        min="0"
                        className="form-control"
                        value={formState.min_lead_time_hours}
                        onChange={(e) => setFormState({ ...formState, min_lead_time_hours: e.target.value })}
                        required
                      />
                      <small className="text-muted">Ej: 4.0 hrs antes del retiro</small>
                    </Col>

                    <Col sm={6} md={4}>
                      <label htmlFor="tf-auto-pct" className="form-label fw-semibold small mb-1">
                        Auto-Aceptar desde (% s/costo)
                      </label>
                      <input
                        id="tf-auto-pct"
                        type="number"
                        step="0.5"
                        className="form-control border-success"
                        value={formState.auto_accept_min_margin_pct}
                        onChange={(e) => setFormState({ ...formState, auto_accept_min_margin_pct: e.target.value })}
                        required
                      />
                      <small className="text-success">Ej: 10% sobre pago conductor</small>
                    </Col>

                    <Col sm={12} md={4}>
                      <label htmlFor="tf-loc-min" className="form-label fw-semibold small mb-1">
                        Alerta Locución (% mín – % máx)
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          id="tf-loc-min"
                          type="number"
                          step="0.5"
                          aria-label="Porcentaje mínimo para alerta a locución"
                          className="form-control border-warning"
                          value={formState.locucion_alert_min_margin_pct}
                          onChange={(e) => setFormState({ ...formState, locucion_alert_min_margin_pct: e.target.value })}
                          required
                        />
                        <span className="text-muted">a</span>
                        <input
                          id="tf-loc-max"
                          type="number"
                          step="0.01"
                          aria-label="Porcentaje máximo para alerta a locución"
                          className="form-control border-warning"
                          value={formState.locucion_alert_max_margin_pct}
                          onChange={(e) => setFormState({ ...formState, locucion_alert_max_margin_pct: e.target.value })}
                          required
                        />
                      </div>
                      <small className="text-muted">Ej: 1% a 9.99% avisa a Locución</small>
                    </Col>

                    <Col sm={6} md={4}>
                      <label htmlFor="tf-usd-rate" className="form-label fw-semibold small mb-1">
                        Tasa USD Manual (opcional)
                      </label>
                      <input
                        id="tf-usd-rate"
                        type="number"
                        step="1"
                        min="0"
                        placeholder={`Auto (${configData?.effective_usd_to_clp_rate || 980})`}
                        className="form-control"
                        value={formState.custom_usd_rate}
                        onChange={(e) => setFormState({ ...formState, custom_usd_rate: e.target.value })}
                      />
                      <small className="text-muted">Vacío = usa tasa global EmiGo</small>
                    </Col>

                    <Col sm={6} md={4}>
                      <label htmlFor="tf-delay-min" className="form-label fw-semibold small mb-1">
                        Lectura Humana (seg mín – máx)
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          id="tf-delay-min"
                          type="number"
                          step="0.2"
                          min="2.0"
                          aria-label="Segundos mínimos de lectura humana"
                          className="form-control"
                          value={formState.human_delay_min_sec}
                          onChange={(e) => setFormState({ ...formState, human_delay_min_sec: e.target.value })}
                          required
                        />
                        <span className="text-muted">–</span>
                        <input
                          id="tf-delay-max"
                          type="number"
                          step="0.2"
                          min="2.5"
                          aria-label="Segundos máximos de lectura humana"
                          className="form-control"
                          value={formState.human_delay_max_sec}
                          onChange={(e) => setFormState({ ...formState, human_delay_max_sec: e.target.value })}
                          required
                        />
                      </div>
                      <small className="text-muted">Recomendado: 3.8s a 6.2s</small>
                    </Col>

                    <Col sm={12} md={4}>
                      <label htmlFor="tf-sync-interval" className="form-label fw-semibold small mb-1">
                        Frecuencia Sync Movio (min)
                      </label>
                      <input
                        id="tf-sync-interval"
                        type="number"
                        min="1"
                        max="60"
                        className="form-control"
                        value={formState.movio_sync_interval_minutes}
                        onChange={(e) => setFormState({ ...formState, movio_sync_interval_minutes: e.target.value })}
                        required
                      />
                      <small className="text-muted">Cada cuántos min consulta Movio</small>
                    </Col>
                  </Row>

                  {/* Zonas / Bases autorizadas */}
                  <div className="mt-3">
                    <div className="form-label fw-semibold small mb-1">Ciudades / Bases Autorizadas para Transfeero</div>
                    <div className="d-flex flex-wrap gap-2">
                      {availableBases.map((b) => {
                        const checked = formState.allowed_base_ids.includes(b.id);
                        return (
                          <button
                            key={b.id}
                            type="button"
                            aria-pressed={checked}
                            onClick={() => handleToggleBase(b.id)}
                            className={`btn btn-sm fw-semibold ${checked ? 'btn-success' : 'btn-outline-secondary'}`}
                          >
                            {checked ? '✓ ' : ''}{b.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </fieldset>

                {/* Mapeo de clases de vehículo */}
                {vehicleTypes.length > 0 && (
                  <fieldset className="mb-4 border-0 p-0 m-0">
                    <legend className="fs-6 fw-bold mb-2 pb-1 border-bottom text-secondary">
                      🚙 Equivalencia de Categorías Transfeero → EmiGo
                    </legend>
                    <Row className="g-2">
                      {['economy', 'standard', 'business', 'first class', 'standard van', 'minibus'].map((tfKey) => (
                        <Col xs={6} md={4} key={tfKey}>
                          <label htmlFor={`map-${tfKey}`} className="form-label small text-capitalize mb-1">
                            {tfKey}
                          </label>
                          <select
                            id={`map-${tfKey}`}
                            className="form-select form-select-sm"
                            value={formState.vehicle_class_mapping?.[tfKey] || 1}
                            onChange={(e) =>
                              setFormState({
                                ...formState,
                                vehicle_class_mapping: {
                                  ...formState.vehicle_class_mapping,
                                  [tfKey]: Number(e.target.value)
                                }
                              })
                            }
                          >
                            {vehicleTypes.map((vt) => (
                              <option key={vt.id} value={vt.id}>
                                {vt.name}
                              </option>
                            ))}
                          </select>
                        </Col>
                      ))}
                    </Row>
                  </fieldset>
                )}

                {/* Credenciales Movio Free y Token Extensión */}
                <fieldset className="mb-4 border-0 p-0 m-0">
                  <legend className="fs-6 fw-bold mb-2 pb-1 border-bottom text-secondary">
                    🔐 Conexión Movio Cloud (Gratis) &amp; Token Extensión
                  </legend>
                  <Row className="g-3">
                    <Col md={6}>
                      <label htmlFor="tf-movio-email" className="form-label fw-semibold small mb-1">
                        Usuario Movio (movio.cloud)
                      </label>
                      <input
                        id="tf-movio-email"
                        type="email"
                        className="form-control"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        value={formState.movio_email}
                        onChange={(e) => setFormState({ ...formState, movio_email: e.target.value })}
                      />
                    </Col>
                    <Col md={6}>
                      <label htmlFor="tf-movio-pwd" className="form-label fw-semibold small mb-1">
                        Clave Movio {configData?.has_movio_password ? '(Guardada ✓ — escribe solo para cambiar)' : '(Requerida)'}
                      </label>
                      <div className="input-group">
                        <input
                          id="tf-movio-pwd"
                          type={showPassword ? 'text' : 'password'}
                          className="form-control"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          placeholder={configData?.has_movio_password ? '••••••••••••' : 'Ingresa clave de Movio'}
                          value={formState.movio_password}
                          onChange={(e) => setFormState({ ...formState, movio_password: e.target.value })}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          aria-pressed={showPassword}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? 'Ocultar' : 'Mostrar'}
                        </button>
                      </div>
                    </Col>

                    <Col xs={12}>
                      <label htmlFor="tf-ext-token" className="form-label fw-semibold small mb-1">
                        Token Secreto Extensión Chrome (Ya viene preconfigurado al descargar el .zip)
                      </label>
                      <div className="input-group input-group-sm">
                        <input
                          id="tf-ext-token"
                          type={showToken ? 'text' : 'password'}
                          className="form-control font-monospace"
                          readOnly
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          value={configData?.extension_api_token || ''}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          aria-pressed={showToken}
                          onClick={() => setShowToken((v) => !v)}
                        >
                          {showToken ? 'Ocultar' : 'Ver'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => {
                            if (configData?.extension_api_token) {
                              navigator.clipboard.writeText(configData.extension_api_token);
                              setFeedback({ variant: 'info', text: 'Token copiado al portapapeles.' });
                            }
                          }}
                        >
                          Copiar
                        </button>
                      </div>
                    </Col>
                  </Row>
                </fieldset>

                <div className="d-flex justify-content-end">
                  <Button type="submit" variant="primary" className="fw-bold px-4" disabled={saving}>
                    {saving ? 'Guardando...' : '💾 Guardar Configuración Transfeero'}
                  </Button>
                </div>
              </form>
            </Card.Body>
          </Card>
        </Col>

        {/* Simulador en Vivo + Guía Rápida */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <form onSubmit={handleSimulate}>
                <fieldset className="border-0 p-0 m-0">
                  <legend className="fs-5 fw-bold mb-3 pb-2 border-bottom">
                    🧪 Simulador de Oferta en Vivo (Dry-Run)
                  </legend>

                  <div className="mb-2">
                    <label htmlFor="sim-pickup" className="form-label small fw-semibold mb-1">
                      Dirección de Origen (Pickup)
                    </label>
                    <input
                      id="sim-pickup"
                      type="text"
                      className="form-control form-control-sm"
                      value={simForm.pickup_address}
                      onChange={(e) => setSimForm({ ...simForm, pickup_address: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label htmlFor="sim-dropoff" className="form-label small fw-semibold mb-1">
                      Dirección de Destino (Dropoff)
                    </label>
                    <input
                      id="sim-dropoff"
                      type="text"
                      className="form-control form-control-sm"
                      value={simForm.dropoff_address}
                      onChange={(e) => setSimForm({ ...simForm, dropoff_address: e.target.value })}
                      required
                    />
                  </div>

                  <Row className="g-2 mb-3">
                    <Col sm={6}>
                      <label htmlFor="sim-dt" className="form-label small fw-semibold mb-1">
                        Fecha y Hora Servicio
                      </label>
                      <input
                        id="sim-dt"
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={simForm.pickup_datetime}
                        onChange={(e) => setSimForm({ ...simForm, pickup_datetime: e.target.value })}
                        required
                      />
                    </Col>
                    <Col sm={3}>
                      <label htmlFor="sim-class" className="form-label small fw-semibold mb-1">
                        Categoría
                      </label>
                      <select
                        id="sim-class"
                        className="form-select form-select-sm"
                        value={simForm.vehicle_class}
                        onChange={(e) => setSimForm({ ...simForm, vehicle_class: e.target.value })}
                      >
                        <option value="Economy">Economy</option>
                        <option value="Standard">Standard</option>
                        <option value="Business">Business</option>
                        <option value="First Class">First Class</option>
                        <option value="Standard Van">Standard Van</option>
                        <option value="Minibus">Minibus</option>
                      </select>
                    </Col>
                    <Col sm={3}>
                      <label htmlFor="sim-price" className="form-label small fw-semibold mb-1">
                        Oferta (USD)
                      </label>
                      <input
                        id="sim-price"
                        type="number"
                        step="0.01"
                        min="1"
                        className="form-control form-control-sm"
                        value={simForm.offer_price}
                        onChange={(e) => setSimForm({ ...simForm, offer_price: e.target.value })}
                        required
                      />
                    </Col>
                  </Row>

                  <Button type="submit" variant="dark" size="sm" className="w-100 fw-bold" disabled={simulating}>
                    {simulating ? 'Calculando tarifa de zona...' : '⚡ Probar Decisión del Centinela'}
                  </Button>
                </fieldset>
              </form>

              {simResult && (
                <div className="mt-3 p-3 rounded border bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold">Resultado Simulación:</span>
                    <Badge
                      bg={DECISION_META[simResult.decision]?.bg || 'secondary'}
                      text={DECISION_META[simResult.decision]?.text}
                    >
                      {DECISION_META[simResult.decision]?.icon || 'ℹ️'}{' '}
                      {DECISION_META[simResult.decision]?.label || simResult.decision}
                    </Badge>
                  </div>
                  <div className="small text-dark mb-1">
                    <strong>Ruta detectada:</strong> {simResult.origin_zone_name || 'Sin zona'} →{' '}
                    {simResult.dest_zone_name || 'Sin zona'} ({simResult.lead_time_hours ?? '—'}h anticipación)
                  </div>
                  <div className="small text-dark mb-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <strong>Oferta en CLP:</strong> {formatCLP(simResult.offer_price_clp)} (TC {simResult.exchange_rate_used}) ·{' '}
                    <strong>Costo Zona Chofer:</strong> {formatCLP(simResult.zone_cost_clp)} ·{' '}
                    <strong>Margen s/costo:</strong>{' '}
                    <span className="fw-bold">
                      {simResult.margin_pct_over_cost !== null && simResult.margin_pct_over_cost !== undefined
                        ? `${simResult.margin_pct_over_cost >= 0 ? '+' : ''}${Number(simResult.margin_pct_over_cost).toFixed(2)}%`
                        : '—'}
                    </span>
                  </div>
                  <div className="small text-muted">{simResult.reason}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm bg-light">
            <Card.Body className="p-3 small">
              <div className="fw-bold mb-2">📌 Instalación en 30 segundos (PC de Locución o tuyo):</div>
              <ol className="mb-0 ps-3">
                <li className="mb-1">
                  Haz clic arriba en <strong>📦 Descargar Extensión Chrome (.zip)</strong> y descomprime la carpeta.
                </li>
                <li className="mb-1">
                  En Chrome abre <code>chrome://extensions</code>, activa <strong>Modo de desarrollador</strong> (esquina superior derecha) y pulsa <strong>Cargar descomprimida</strong> seleccionando la carpeta.
                </li>
                <li>
                  Deja abierta la pestaña <code>control.transfeero.com</code> en <strong>Available</strong>. El Centinela ya trae tu Token preconfigurado y aceptará con pausa humana (3.8s–6.2s) sin usar bots prohibidos.
                </li>
              </ol>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla Responsiva de Auditoría de Ofertas */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0 fw-bold">📋 Bitácora de Ofertas Evaluadas en Transfeero</h5>
            <div className="d-flex gap-2 align-items-center">
              <label htmlFor="tf-log-filter" className="small fw-semibold text-muted mb-0">
                Filtrar decisión:
              </label>
              <select
                id="tf-log-filter"
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={decisionFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setDecisionFilter(val);
                  loadAll(val);
                }}
              >
                <option value="">Todas las decisiones</option>
                <option value="AUTO_ACCEPT">✅ Auto-Aceptadas</option>
                <option value="ALERT_LOCUCION">🚨 Alertas a Locución</option>
                <option value="REJECT_MARGIN">⛔ Bajo Costo (&lt;1%)</option>
                <option value="REJECT_LEAD_TIME">⏱️ Poca Anticipación</option>
                <option value="REJECT_ZONE">📍 Fuera de Zona</option>
              </select>
            </div>
          </div>

          <div
            tabIndex={0}
            role="region"
            aria-labelledby="tf-audit-caption"
            className="table-responsive border rounded"
            style={{ maxHeight: '480px', overflow: 'auto' }}
          >
            <table className="table table-hover table-sm align-middle mb-0">
              <caption id="tf-audit-caption" className="visually-hidden">
                Historial de auditoría de ofertas de Transfeero evaluadas contra el tarifario de zonas de EmiGo
              </caption>
              <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th scope="col">Evaluada</th>
                  <th scope="col">Oferta / Ref</th>
                  <th scope="col">Retiro (CL)</th>
                  <th scope="col">Ruta / Zonas Detectadas</th>
                  <th scope="col">Categoría</th>
                  <th scope="col" className="text-end">Oferta USD</th>
                  <th scope="col" className="text-end">Equiv. CLP</th>
                  <th scope="col" className="text-end">Costo Zona</th>
                  <th scope="col" className="text-end">Margen</th>
                  <th scope="col">Decisión</th>
                </tr>
              </thead>
              <tbody>
                {(!logsPayload.items || logsPayload.items.length === 0) ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      Aún no se han registrado ofertas en vivo desde la pestaña Available de Transfeero.
                    </td>
                  </tr>
                ) : (
                  logsPayload.items.map((item) => {
                    const meta = DECISION_META[item.decision] || { icon: 'ℹ️', label: item.decision, bg: 'secondary' };
                    return (
                      <tr key={item.id}>
                        <td className="small text-nowrap">{item.created_at_cl || '—'}</td>
                        <td className="fw-semibold text-nowrap">
                          #{item.external_offer_id}
                          {item.trip_id && (
                            <div>
                              <Badge bg="dark" className="mt-1">Viaje #{item.trip_id}</Badge>
                            </div>
                          )}
                        </td>
                        <td className="small text-nowrap">
                          {item.pickup_datetime_cl || '—'}
                          {item.lead_time_hours !== null && item.lead_time_hours !== undefined && (
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                              ({Number(item.lead_time_hours).toFixed(1)}h antes)
                            </div>
                          )}
                        </td>
                        <td className="small" style={{ minWidth: '220px', maxWidth: '300px' }}>
                          <div className="fw-semibold text-truncate" title={`${item.pickup_address} → ${item.dropoff_address}`}>
                            {item.origin_zone_name || '?'} → {item.dest_zone_name || '?'}
                          </div>
                          <div className="text-muted text-truncate" style={{ fontSize: '0.73rem' }}>
                            {item.pickup_address} → {item.dropoff_address}
                          </div>
                        </td>
                        <td className="small text-nowrap">{item.vehicle_class || '—'}</td>
                        <td className="text-end text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatUSD(item.offer_price_usd, item.offer_currency)}
                        </td>
                        <td className="text-end text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCLP(item.offer_price_clp)}
                        </td>
                        <td className="text-end text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCLP(item.zone_cost_clp)}
                        </td>
                        <td
                          className={`text-end fw-bold text-nowrap ${
                            (item.margin_pct_over_cost ?? -99) >= 10
                              ? 'text-success'
                              : (item.margin_pct_over_cost ?? -99) >= 1
                              ? 'text-warning'
                              : 'text-danger'
                          }`}
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {item.margin_pct_over_cost !== null && item.margin_pct_over_cost !== undefined
                            ? `${item.margin_pct_over_cost >= 0 ? '+' : ''}${Number(item.margin_pct_over_cost).toFixed(1)}%`
                            : '—'}
                        </td>
                        <td className="text-nowrap">
                          <Badge bg={meta.bg} text={meta.text} title={item.decision_reason || ''}>
                            {meta.icon} {meta.label}
                          </Badge>
                          {item.accepted_in_portal && (
                            <Badge bg="primary" className="ms-1">✓ Clic Portal</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TransfeeroManagerView;
